import { lazy, Suspense, useEffect, useRef } from 'react';
import { Alert, Skeleton } from '@/ui/primitives';
import { CepForm } from '@/components/CepForm';
import { NotifyInline } from '@/components/NotifyInline';
import { ResultPanel } from '@/components/ResultPanel';
import { StationList } from '@/components/StationList';
import type { StationFinder } from '@/hooks/useStationFinder';
import { notifyHostOfHeight } from '@/services/embedHost';

const StationMap = lazy(() =>
  import('@/components/StationMap').then((module) => ({ default: module.StationMap })),
);

/* The map is the reason the section exists, so it never waits for an answer and never
   collapses below this. Above 640px the panel sits beside it and the map takes the height
   of whatever the panel needs; below, the map is a band above the panel. */
const STACKED_MAP_HEIGHT = 'h-[clamp(200px,60vw,300px)]';
const ROW_MIN_HEIGHT = 'sm:min-h-[460px]';

/* The page inside the vammo.com home, in place of the pit stop section. It carries no
   heading and no language switch: the section around it already has a heading, and the
   host page is written in one language, which reaches this page as `lang`. */
export const EmbedLayout = ({
  locale,
  dict,
  stations,
  state,
  result,
  errorKind,
  submitCep,
  useLocation,
  mapFailed,
  onMapUnsupported,
  nearbyStations,
  notifyIntent,
  submitNotify,
}: StationFinder) => {
  const root = useRef<HTMLDivElement | null>(null);

  /* The host gives this page a band of fixed height, and the longest answer does not fit
     in it. Rather than cut the answer, report what the content grew to and let the host
     grow its section. Measured off the root, whose own minimum is a constant — measuring
     something sized by the iframe would report the height back to itself for ever. */
  useEffect(() => {
    const element = root.current;
    if (!element) {
      return;
    }
    const report = () => {
      notifyHostOfHeight(element.scrollHeight);
    };
    report();
    const observer = new ResizeObserver(report);
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div
      ref={root}
      className={`flex w-full flex-col bg-white text-black sm:flex-row ${ROW_MIN_HEIGHT}`}
    >
      {mapFailed ? null : (
        <div className={`relative w-full shrink-0 ${STACKED_MAP_HEIGHT} sm:h-auto sm:flex-1`}>
          <Suspense fallback={<Skeleton className="absolute inset-0 rounded-none" />}>
            <StationMap
              surface="embed"
              dict={dict}
              stations={stations}
              origin={result?.coords}
              nearby={nearbyStations}
              onUnsupported={onMapUnsupported}
            />
          </Suspense>
        </div>
      )}

      <div className="flex w-full flex-col gap-3 border-grey-200 p-4 sm:w-[clamp(300px,44%,420px)] sm:shrink-0 sm:border-l sm:p-5">
        {result ? null : (
          <p className="text-[13px] leading-snug text-grey-800">{dict.hero.subtitle}</p>
        )}

        <CepForm
          variant="compact"
          dict={dict}
          onSubmit={submitCep}
          onUseLocation={useLocation}
          isBusy={state === 'busy'}
        />

        {state === 'error' && errorKind ? (
          <Alert variant={errorKind === 'imprecise' ? 'warning' : 'destructive'}>
            {dict.errors[errorKind]}
          </Alert>
        ) : null}

        {result ? (
          <ResultPanel
            /* Remount per answer: without a changing key the reveal plays once and every
               later lookup appears with no transition at all. */
            key={`${result.cep ?? 'geo'}-${result.nearest.address}`}
            density="compact"
            dict={dict}
            locale={locale}
            result={result}
            action={
              notifyIntent === null ? undefined : (
                <NotifyInline
                  dict={dict}
                  intent={notifyIntent}
                  band={result.band}
                  inputType={result.cep === null ? 'geolocation' : 'cep'}
                  onSubmit={submitNotify}
                />
              )
            }
          />
        ) : null}

        {/* The list stands in for the map only where the browser cannot draw one, and only
            once there is something to list. */}
        {mapFailed && result ? (
          <StationList dict={dict} locale={locale} stations={nearbyStations} isMapFallback />
        ) : null}

        {/* Same notice as the standalone page, at the same size. The data leaving this
            page did not shrink with the layout. */}
        <p className="mt-auto pt-1 text-[11px] leading-snug text-grey-800">
          {dict.footer.privacy}
        </p>
      </div>
    </div>
  );
};
